import React, { useCallback, useRef, useState } from 'react'
import { Upload, HelpCircle, Paperclip } from 'lucide-react'
import { Tooltip } from 'antd'
import { TYPOGRAPHY } from '../../../styles/theme'
import { NodeInteractionZone } from './Base/NodeInteractionZone'
import { useVentureDocuments } from './hooks/useVentureDocuments'
import { DocumentService } from '../../../services/documents/documentService'
import { VentureDocumentList } from './VentureDocumentList'
import { DocumentClassificationModal } from './DocumentClassificationModal'
import {
  FieldContainer,
  FieldLabel,
  DocumentUploadArea,
  DocumentUploadText,
} from './styles/VentureNodeStyles'

const MAX_DOCUMENTS = 10

interface VentureDocumentUploadProps {
  ventureNodeId: string
  mindmapId: string
  isReadOnly: boolean
}

export const VentureDocumentUpload = React.memo(
  ({ ventureNodeId, mindmapId, isReadOnly }: VentureDocumentUploadProps) => {
    const { documents, isUploading, isLoaded, error, setError, uploadDocument, deleteDocument } =
      useVentureDocuments(ventureNodeId, mindmapId)

    const fileInputRef = useRef<HTMLInputElement>(null)
    const [pendingFiles, setPendingFiles] = useState<File[]>([])

    const handleFileSelect = useCallback(
      (files: FileList | null) => {
        if (!files || files.length === 0) return
        setError(null)
        setPendingFiles(Array.from(files))
      },
      [setError]
    )

    const handleModalConfirm = useCallback(
      async (classification: string) => {
        const files = pendingFiles
        setPendingFiles([])
        for (const file of files) {
          await uploadDocument(file, classification)
        }
      },
      [pendingFiles, uploadDocument]
    )

    const handleModalCancel = useCallback(() => {
      setPendingFiles([])
      if (fileInputRef.current) fileInputRef.current.value = ''
    }, [])

    const handleDrop = useCallback(
      (e: React.DragEvent) => {
        e.preventDefault()
        handleFileSelect(e.dataTransfer.files)
      },
      [handleFileSelect]
    )

    const handleDragOver = useCallback((e: React.DragEvent) => {
      e.preventDefault()
    }, [])

    const handleClick = useCallback(() => {
      fileInputRef.current?.click()
    }, [])

    const handleInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        handleFileSelect(e.target.files)
        if (fileInputRef.current) fileInputRef.current.value = ''
      },
      [handleFileSelect]
    )

    if (isReadOnly) return null
    if (!isLoaded) return null

    const canUploadMore = documents.length < MAX_DOCUMENTS && !isUploading
    const acceptTypes = DocumentService.ALLOWED_EXTENSIONS.join(',')

    return (
      <NodeInteractionZone>
        <FieldContainer $padding="8px 12px 12px 12px">
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <Paperclip
              size={11}
              style={{ color: 'var(--color-text-secondary)', flexShrink: 0, opacity: 0.6 }}
            />
            <FieldLabel $color="var(--color-text-secondary)" style={{ marginBottom: 0 }}>
              Supporting Files
            </FieldLabel>
            <Tooltip
              title="Upload research, business plans, presentations, user feedback, or any files relevant to this venture. The AI will use these to generate more grounded and informed suggestions."
              mouseEnterDelay={0.2}
              placement="top"
              styles={{ root: { maxWidth: 280 } }}
            >
              <HelpCircle
                size={12}
                style={{ color: 'var(--color-text-tertiary)', cursor: 'default', flexShrink: 0 }}
              />
            </Tooltip>
          </div>

          <VentureDocumentList documents={documents} onDelete={deleteDocument} />

          {canUploadMore && (
            <DocumentUploadArea
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onClick={handleClick}
              style={{ marginTop: documents.length > 0 ? 8 : 6 }}
            >
              <Upload size={16} style={{ opacity: 0.4, marginBottom: 4 }} />
              <DocumentUploadText>
                Drop PDF, DOCX, or TXT files here
                <br />
                <span style={{ opacity: 0.7 }}>or click to browse</span>
              </DocumentUploadText>
              <input
                ref={fileInputRef}
                type="file"
                accept={acceptTypes}
                multiple
                style={{ display: 'none' }}
                onChange={handleInputChange}
              />
            </DocumentUploadArea>
          )}

          {error && (
            <div style={{ fontSize: TYPOGRAPHY.caption.fontSize, marginTop: 4 }}>{error}</div>
          )}

          {isUploading && (
            <div
              style={{
                fontSize: TYPOGRAPHY.caption.fontSize,
                color: 'var(--color-text-muted)',
                marginTop: 4,
              }}
            >
              Uploading...
            </div>
          )}
        </FieldContainer>

        <DocumentClassificationModal
          files={pendingFiles}
          onConfirm={(classification) => void handleModalConfirm(classification)}
          onCancel={handleModalCancel}
        />
      </NodeInteractionZone>
    )
  }
)

VentureDocumentUpload.displayName = 'VentureDocumentUpload'
